// Posting › Matched deposits — the mock behind the Matched tab, the Bank
// deposits coverage note and "See uploads" (rcm-32, D193/D194).
//
// One seed, so the numbers add up everywhere:
//  - every EFT deposit is built from real claims in the claim set
//    (claims-review historyMock): a claim shows up in exactly one deposit, with
//    the paid amount its own record shows, so the claims table total equals the
//    deposit and /claims/record/:id agrees;
//  - check deposits are hand-written (paper EOBs carry no claim numbers here);
//  - statements cover Jul 1 – Oct 14 with one hole (Aug 1–14); no deposit falls
//    outside a statement or after Oct 18;
//  - Oct 1–14 kept deposits = matched + don't match (the four on Bank deposits).

import { HISTORY_CLAIMS, type HistoryClaim } from '@/features/claims-review/historyMock';
import type {
  BankAccount,
  MatchedCheck,
  MatchedClaim,
  MatchedDeposit,
  MatchedEra,
  MatchKind,
  UploadRecord,
} from './types';
import {
  addDays,
  isoFromLabel,
  isWeekend,
  shortDate,
  subBusinessDays,
} from './dates';

// ---------------------------------------------------------------------------
// small helpers
// ---------------------------------------------------------------------------

function hash(s: string): number {
  let n = 2166136261;
  for (let i = 0; i < s.length; i++) {
    n ^= s.charCodeAt(i);
    n = Math.imul(n, 16777619);
  }
  return n >>> 0;
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
function digits(seed: string, len: number): string {
  let out = '';
  let h = hash(seed);
  while (out.length < len) {
    out += String(h % 10);
    h = Math.imul(h, 1103515245) + 12345;
    h >>>= 0;
  }
  return out.slice(0, len);
}
/** An ERA trace number, "1T5520174"-shaped. */
function traceFor(seed: string): string {
  return `1T${digits(`t|${seed}`, 7)}`;
}
function clockLabel(minutes: number): string {
  const h24 = Math.floor(minutes / 60);
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h)}:${String(minutes % 60).padStart(2, '0')} ${h24 >= 12 ? 'PM' : 'AM'}`;
}

export const ADMIN_NAME = 'Ava Thompson (admin)';
const UPLOADERS = [ADMIN_NAME, 'Ryan Cole', 'Maria Flores', ADMIN_NAME, 'Dev Patel'] as const;

export const CHASE: BankAccount = { id: 'chase-4417', name: 'Chase business checking ··4417', short: 'Chase ··4417' };
export const WELLS: BankAccount = { id: 'wells-9021', name: 'Wells Fargo business checking ··9021', short: 'Wells Fargo ··9021' };

/** The statements start Jul 1 and end Oct 14; Aug 1–14 was never uploaded. */
export const COVER_FROM = '2026-07-01';
export const COVER_TO = '2026-10-14';
export const HOLE_FROM = '2026-08-01';
export const HOLE_TO = '2026-08-14';
export const OCT_FILE = { from: '2026-10-01', to: '2026-10-14' } as const;

function covered(iso: string): boolean {
  return iso >= COVER_FROM && iso <= COVER_TO && !(iso >= HOLE_FROM && iso <= HOLE_TO);
}

// ---------------------------------------------------------------------------
// payer → how the bank writes the line
// ---------------------------------------------------------------------------

interface PayerBank {
  display: string;
  /** The bank line carries the ERA's trace number (D194: trace match). */
  traceOnLine: boolean;
  token: string;
}
const BANK: Record<string, PayerBank> = {
  MetLife: { display: 'MetLife', traceOnLine: true, token: 'METLIFE DENTAL' },
  'Delta Dental': { display: 'Delta Dental of Texas', traceOnLine: true, token: 'DELTA DENTAL TX' },
  'Delta Dental of Texas': { display: 'Delta Dental of Texas', traceOnLine: true, token: 'DELTA DENTAL TX' },
  Aetna: { display: 'Aetna', traceOnLine: true, token: 'AETNA' },
  Guardian: { display: 'Guardian', traceOnLine: true, token: 'GUARDIAN LIFE' },
  Cigna: { display: 'Cigna', traceOnLine: false, token: 'CIGNA' },
  'BCBS of Texas': { display: 'BCBS of Texas', traceOnLine: false, token: 'BCBSTX DENTAL' },
  UnitedHealthcare: { display: 'UnitedHealthcare', traceOnLine: false, token: 'UHC DENTAL' },
  Humana: { display: 'Humana', traceOnLine: false, token: 'HUMANA DENTAL' },
  'Sun Life': { display: 'Sun Life', traceOnLine: false, token: 'SUN LIFE ASSURANCE' },
};
function bankOf(payer: string): PayerBank {
  return BANK[payer] ?? { display: payer, traceOnLine: false, token: payer.toUpperCase() };
}

function lineWithTrace(token: string, trace: string, seed: string): { line: string; hl: string } {
  return { line: `${token} HCCLAIMPMT CCD TRN*1*${trace}*${digits(`id|${seed}`, 10)}`, hl: `TRN*1*${trace}` };
}
function lineWithoutTrace(token: string, seed: string): { line: string; hl: string } {
  return { line: `${token} HCCLAIMPMT CCD ${digits(`id|${seed}`, 10)}`, hl: token };
}

// ---------------------------------------------------------------------------
// real claims → deposits
// ---------------------------------------------------------------------------

function eventIso(c: HistoryClaim, re: RegExp): string | undefined {
  const e = [...c.timeline].reverse().find((x) => re.test(x.lead));
  return e?.iso;
}

interface Group {
  payer: string;
  iso: string;
  claims: HistoryClaim[];
}

/** Real "deposited" claims grouped by payer and the day their deposit matched. */
function realGroups(): Group[] {
  const map = new Map<string, Group>();
  for (const c of HISTORY_CLAIMS) {
    if (c.status !== 'deposited' || c.paid == null || c.paid <= 0 || !c.payerPaidDate) continue;
    if (!BANK[c.payer]) continue;
    const iso = eventIso(c, /Matched the deposit/i);
    if (!iso || isWeekend(iso)) continue;
    if (iso < COVER_FROM || iso > '2026-10-16') continue;
    const key = `${c.payer}|${iso}`;
    const g = map.get(key) ?? { payer: c.payer, iso, claims: [] };
    g.claims.push(c);
    map.set(key, g);
  }
  return [...map.values()];
}

function postedOf(claims: HistoryClaim[]): { label: string; iso: string; by: string } {
  let best: { label: string; iso: string; by: string } | null = null;
  for (const c of claims) {
    const e = c.timeline.find((x) => /^Posted/i.test(x.lead));
    if (!e?.iso) continue;
    if (!best || e.iso > best.iso || (e.iso === best.iso && e.stamp > best.label)) {
      best = { label: e.stamp, iso: e.iso, by: e.who };
    }
  }
  return best ?? { label: '', iso: '', by: 'ELVA' };
}

function claimsOf(cs: HistoryClaim[]): MatchedClaim[] {
  return [...cs]
    .sort((a, b) => (a.id < b.id ? -1 : 1))
    .map((c) => ({ patient: c.patient, claimId: c.id, paid: c.paid ?? 0 }));
}

function searchTextOf(d: Omit<MatchedDeposit, 'searchText'>): string {
  const parts: string[] = [d.payer, d.bankLine, d.id];
  const amt = d.amount.toFixed(2);
  parts.push(amt, `$${d.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
  for (const e of d.eras) {
    parts.push(e.trace);
    for (const c of e.claims) parts.push(c.patient, c.claimId ?? '');
  }
  for (const c of d.checks) parts.push(c.payer, c.checkNumber);
  if (d.bankTrace) parts.push(d.bankTrace);
  return parts.join(' ').toLowerCase();
}

function finish(d: Omit<MatchedDeposit, 'searchText' | 'statementFile' | 'uploadedBy' | 'uploadedLabel'>): MatchedDeposit {
  const full = { ...d, statementFile: '', uploadedBy: '', uploadedLabel: '' };
  return { ...full, searchText: searchTextOf(full) };
}

function depositFromGroup(g: Group): MatchedDeposit {
  const bank = bankOf(g.payer);
  const byEra = new Map<string, HistoryClaim[]>();
  for (const c of g.claims) {
    const key = c.payerPaidDate ?? '';
    byEra.set(key, [...(byEra.get(key) ?? []), c]);
  }
  const labels = [...byEra.keys()].sort((a, b) => (isoFromLabel(a) < isoFromLabel(b) ? -1 : 1));
  const n = labels.length;
  const eras: MatchedEra[] = labels.map((lab, j) => {
    const cs = byEra.get(lab) ?? [];
    const k = n > 1 ? n - j : 1 + (hash(`${g.payer}|${g.iso}|k`) % 3);
    const claims = claimsOf(cs);
    const posted = postedOf(cs);
    return {
      dateIso: subBusinessDays(g.iso, k),
      trace: traceFor(`${g.payer}|${g.iso}|${String(j)}`),
      amount: round2(claims.reduce((s, c) => s + c.paid, 0)),
      claims,
      postedLabel: posted.label,
      postedIso: posted.iso,
      postedBy: posted.by,
    };
  });
  const amount = round2(eras.reduce((s, e) => s + e.amount, 0));
  const kind: MatchKind = n > 1 ? 'eras' : bank.traceOnLine ? 'trace' : 'amount';
  const first = eras[0];
  const onLine = kind === 'trace' && first ? lineWithTrace(bank.token, first.trace, `${g.payer}|${g.iso}`) : lineWithoutTrace(bank.token, `${g.payer}|${g.iso}`);
  return finish({
    id: `MD-${g.iso.replace(/-/g, '')}-${g.payer.toLowerCase().replace(/[^a-z]/g, '')}`,
    kind,
    dateIso: g.iso,
    payer: bank.display,
    amount,
    accountId: CHASE.id,
    bankLine: onLine.line,
    bankHighlight: onLine.hl,
    bankTrace: kind === 'trace' && first ? first.trace : null,
    bankItems: null,
    eras,
    checks: [],
  });
}

// ---------------------------------------------------------------------------
// check deposits (hand-written)
// ---------------------------------------------------------------------------

interface CheckSeed {
  iso: string;
  line: string;
  hl: string;
  items: number | null;
  receivedFrom: string;
  checks: [string, string, number, number][]; // payer, check number, claim count, amount
  postedClock: string;
  postedBy: string;
}

const CHECK_SEEDS: CheckSeed[] = [
  {
    iso: '2026-10-13', line: 'REMOTE DEPOSIT 3 ITEMS', hl: 'REMOTE DEPOSIT', items: 3, receivedFrom: '2026-10-09',
    checks: [['Guardian', '004398215', 5, 2373.24], ['Ameritas', '0091123', 2, 611.86], ['Principal', '7712004', 1, 255.0]],
    postedClock: '2:36 PM', postedBy: 'ELVA',
  },
  {
    iso: '2026-09-22', line: 'REMOTE DEPOSIT 2 ITEMS', hl: 'REMOTE DEPOSIT', items: 2, receivedFrom: '2026-09-17',
    checks: [['Delta Dental of Texas', '00553108', 4, 1046.12], ['Principal', '7704961', 2, 388.4]],
    postedClock: '3:08 PM', postedBy: 'ELVA',
  },
  {
    iso: '2026-09-09', line: 'MOBILE DEPOSIT', hl: 'MOBILE DEPOSIT', items: null, receivedFrom: '2026-09-04',
    checks: [['Guardian', '004371160', 3, 917.3]],
    postedClock: '11:20 AM', postedBy: 'Ryan Cole',
  },
  {
    iso: '2026-08-26', line: 'REMOTE DEPOSIT 3 ITEMS', hl: 'REMOTE DEPOSIT', items: 3, receivedFrom: '2026-08-20',
    checks: [['Ameritas', '0088417', 1, 284.15], ['Principal', '7697720', 3, 702.0], ['Guardian', '004352907', 4, 1155.35]],
    postedClock: '1:52 PM', postedBy: 'ELVA',
  },
  {
    iso: '2026-08-18', line: 'REMOTE DEPOSIT 2 ITEMS', hl: 'REMOTE DEPOSIT', items: 2, receivedFrom: '2026-08-14',
    checks: [['Delta Dental of Texas', '00549277', 2, 561.8], ['Ameritas', '0087602', 1, 198.45]],
    postedClock: '10:14 AM', postedBy: 'ELVA',
  },
  {
    iso: '2026-07-28', line: 'MOBILE DEPOSIT', hl: 'MOBILE DEPOSIT', items: null, receivedFrom: '2026-07-22',
    checks: [['Principal', '7681135', 2, 473.6]],
    postedClock: '4:41 PM', postedBy: 'ELVA',
  },
  {
    iso: '2026-07-14', line: 'REMOTE DEPOSIT 2 ITEMS', hl: 'REMOTE DEPOSIT', items: 2, receivedFrom: '2026-07-09',
    checks: [['Guardian', '004318846', 3, 842.9], ['Ameritas', '0084310', 1, 156.0]],
    postedClock: '2:03 PM', postedBy: 'ELVA',
  },
];

function depositFromCheckSeed(s: CheckSeed): MatchedDeposit {
  const checks: MatchedCheck[] = s.checks.map(([payer, checkNumber, claimCount, amount], i) => ({
    payer,
    checkNumber,
    claimCount,
    amount,
    receivedIso: i === s.checks.length - 1 ? s.iso : addDays(s.receivedFrom, i),
  }));
  const amount = round2(checks.reduce((t, c) => t + c.amount, 0));
  return finish({
    id: `MD-${s.iso.replace(/-/g, '')}-checks`,
    kind: 'checks',
    dateIso: s.iso,
    payer: 'Checks deposited',
    amount,
    accountId: CHASE.id,
    bankLine: s.line,
    bankHighlight: s.hl,
    bankTrace: null,
    bankItems: s.items,
    eras: [],
    checks,
  });
}

/** When the checks of a deposit were posted (all on the deposit day). */
export function checkPostedLabel(d: MatchedDeposit): string {
  const seed = CHECK_SEEDS.find((s) => s.iso === d.dateIso);
  return `${shortDate(d.dateIso)}, ${seed?.postedClock ?? '2:00 PM'}`;
}
export function checkPostedBy(d: MatchedDeposit): string {
  return CHECK_SEEDS.find((s) => s.iso === d.dateIso)?.postedBy ?? 'ELVA';
}

// ---------------------------------------------------------------------------
// the four deposits that don't match (Bank deposits) and what they become
// when they get settled (they move to Matched)
// ---------------------------------------------------------------------------

export const DM = {
  /** BD-1: money in, nothing posted. Its 5 claims are in To post. */
  delta: { id: 'BD-1', iso: '2026-10-09', payer: 'Delta Dental of Texas', amount: 1312.4, trace: '1T740588190' },
  /** BD-3: the deposit is $23.88 less than its ERA (an older claim was taken back). */
  metlife: {
    id: 'BD-3', iso: '2026-10-13', eraIso: '2026-10-12', payer: 'MetLife', amount: 1186.12, eraAmount: 1210.0, trace: '1T93120477',
    claims: [{ patient: 'Owen Barnes', paid: 1210.0 }],
    takeback: { patient: 'Noah Kim', amount: -23.88 },
    postedLabel: 'Oct 12, 3:46 PM',
  },
  /** BD-4: the deposit is $4.18 more than its ERA (interest). */
  aetna: {
    id: 'BD-4', iso: '2026-10-07', eraIso: '2026-10-06', payer: 'Aetna', amount: 604.18, eraAmount: 600.0, trace: '1T551209763',
    claim: { patient: 'Priya Shah', paid: 600.0 }, interest: 4.18, postedLabel: 'Oct 6, 1:19 PM',
  },
  /** BD-5: two open Cigna payments of $642.00 could fit this one deposit. */
  cigna: {
    id: 'BD-5', iso: '2026-10-14', payer: 'Cigna', amount: 642.0,
    a: { eraIso: '2026-10-08', patient: 'Daniel Cruz', trace: '1T4410287', postedLabel: 'Oct 8, 2:27 PM' },
    b: { eraIso: '2026-10-09', patient: 'Ellie Ramos', trace: '1T4410553', postedLabel: 'Oct 9, 10:52 AM' },
  },
} as const;

export const DM_TOTAL = round2(DM.delta.amount + DM.metlife.amount + DM.aetna.amount + DM.cigna.amount);

function synthClaim(patient: string, paid: number): MatchedClaim {
  return { patient, claimId: null, paid };
}

/** The Matched deposit a settled "don't match" turns into. */
export function matchedFromDifference(id: string, pick?: 'a' | 'b'): MatchedDeposit | null {
  if (id === DM.metlife.id) {
    const m = DM.metlife;
    const era: MatchedEra = {
      dateIso: m.eraIso,
      trace: m.trace,
      amount: m.eraAmount,
      claims: m.claims.map((c) => synthClaim(c.patient, c.paid)),
      extras: [{ label: `Taken back on ${m.takeback.patient}’s Aug 12 claim`, amount: m.takeback.amount }],
      postedLabel: m.postedLabel,
      postedIso: m.eraIso,
      postedBy: 'ELVA',
    };
    const l = lineWithTrace('METLIFE DENTAL', m.trace, 'bd3');
    return finish({
      id: 'MD-20261013-metlife-fixed', kind: 'trace', dateIso: m.iso, payer: 'MetLife', amount: m.amount, accountId: CHASE.id,
      bankLine: l.line, bankHighlight: l.hl, bankTrace: m.trace, bankItems: null, eras: [era], checks: [],
    });
  }
  if (id === DM.aetna.id) {
    const a = DM.aetna;
    const era: MatchedEra = {
      dateIso: a.eraIso,
      trace: a.trace,
      amount: a.eraAmount,
      claims: [synthClaim(a.claim.patient, a.claim.paid)],
      extras: [{ label: 'Interest for paying late', amount: a.interest }],
      postedLabel: a.postedLabel,
      postedIso: a.eraIso,
      postedBy: 'ELVA',
    };
    const l = lineWithTrace('AETNA', a.trace, 'bd4');
    return finish({
      id: 'MD-20261007-aetna-fixed', kind: 'trace', dateIso: a.iso, payer: 'Aetna', amount: a.amount, accountId: CHASE.id,
      bankLine: l.line, bankHighlight: l.hl, bankTrace: a.trace, bankItems: null, eras: [era], checks: [],
    });
  }
  if (id === DM.cigna.id && pick) {
    const c = DM.cigna;
    const o = pick === 'a' ? c.a : c.b;
    const era: MatchedEra = {
      dateIso: o.eraIso, trace: o.trace, amount: c.amount, claims: [synthClaim(o.patient, c.amount)],
      postedLabel: o.postedLabel, postedIso: o.eraIso, postedBy: 'ELVA',
    };
    const l = lineWithoutTrace('CIGNA', 'bd5');
    return finish({
      id: 'MD-20261014-cigna-picked', kind: 'amount', dateIso: c.iso, payer: 'Cigna', amount: c.amount, accountId: CHASE.id,
      bankLine: l.line, bankHighlight: l.hl, bankTrace: null, bankItems: null, eras: [era], checks: [], pickedBy: ADMIN_NAME,
    });
  }
  return null;
}

/** Posted claims that belong to the four "don't match" deposits (feeds the Posted list). */
export const DM_POSTED: { patient: string; payer: string; amount: number; iso: string; trace: string }[] = [
  ...DM.metlife.claims.map((c) => ({ patient: c.patient, payer: 'MetLife', amount: c.paid, iso: DM.metlife.eraIso, trace: DM.metlife.trace })),
  { patient: DM.aetna.claim.patient, payer: 'Aetna', amount: DM.aetna.claim.paid, iso: DM.aetna.eraIso, trace: DM.aetna.trace },
  { patient: DM.cigna.a.patient, payer: 'Cigna', amount: DM.cigna.amount, iso: DM.cigna.a.eraIso, trace: DM.cigna.a.trace },
  { patient: DM.cigna.b.patient, payer: 'Cigna', amount: DM.cigna.amount, iso: DM.cigna.b.eraIso, trace: DM.cigna.b.trace },
];

// ---------------------------------------------------------------------------
// a second bank account, added with the "new account" upload (Add it)
// ---------------------------------------------------------------------------

function depositForAccount(
  accountId: string, iso: string, payer: string, kind: 'trace' | 'amount', claims: [string, number][], eraIso: string, postedLabel: string,
): MatchedDeposit {
  const bank = bankOf(payer);
  const cl = claims.map(([p, a]) => synthClaim(p, a));
  const amount = round2(cl.reduce((s, c) => s + c.paid, 0));
  const trace = traceFor(`${accountId}|${payer}|${iso}`);
  const l = kind === 'trace' ? lineWithTrace(bank.token, trace, `${accountId}|${iso}`) : lineWithoutTrace(bank.token, `${accountId}|${iso}`);
  return finish({
    id: `MD-${iso.replace(/-/g, '')}-${payer.toLowerCase().replace(/[^a-z]/g, '')}-${accountId.slice(0, 2)}`,
    kind, dateIso: iso, payer: bank.display, amount, accountId,
    bankLine: l.line, bankHighlight: l.hl, bankTrace: kind === 'trace' ? trace : null, bankItems: null,
    eras: [{ dateIso: eraIso, trace, amount, claims: cl, postedLabel, postedIso: isoFromLabel(postedLabel), postedBy: 'ELVA' }],
    checks: [],
  });
}

export function buildNewAccountDeposits(): MatchedDeposit[] {
  return [
    depositForAccount(WELLS.id, '2026-10-01', 'Cigna', 'amount', [['Iris Moreno', 310], ['Jonah Pratt', 286], ['Lena Ortiz', 246]], '2026-09-29', 'Sep 28, 3:12 PM'),
    depositForAccount(WELLS.id, '2026-10-02', 'Delta Dental of Texas', 'trace', [['Ruth Allen', 250], ['Colin Reed', 168.5]], '2026-09-30', 'Sep 29, 11:48 AM'),
    depositForAccount(WELLS.id, '2026-10-02', 'MetLife', 'trace', [['Tara Bloom', 296]], '2026-10-01', 'Sep 30, 4:05 PM'),
  ];
}

// ---------------------------------------------------------------------------
// statements ("See uploads")
// ---------------------------------------------------------------------------

function slugDay(iso: string): string {
  return shortDate(iso).toLowerCase().replace(' ', '-');
}

function uploadWhen(iso: string, seed: string): string {
  const mins = 8 * 60 + (hash(seed) % (9 * 60));
  return `${shortDate(iso)}, ${clockLabel(mins)}`;
}
function nextBusinessDay(iso: string): string {
  let cur = addDays(iso, 1);
  while (isWeekend(cur)) cur = addDays(cur, 1);
  return cur;
}

/** The Oct 1–14 upload, the one the board describes (412 lines, uploaded Oct 14). */
export const OCT_UPLOAD_FILE = 'chase-4417-oct-1-14.csv';

/**
 * Builds the uploads (newest first) from the deposits. Older statements come
 * in 4-day files; the Oct 1–14 one is a single file of 412 lines.
 */
export function buildUploads(deposits: MatchedDeposit[], dontMatch: { iso: string; amount: number }[]): UploadRecord[] {
  const spans: [string, string][] = [];
  const chunk = (from: string, to: string): void => {
    let cur = from;
    while (cur <= to) {
      const end = addDays(cur, 3) > to ? to : addDays(cur, 3);
      spans.push([cur, end]);
      cur = addDays(end, 1);
    }
  };
  chunk('2026-07-01', '2026-07-31');
  chunk('2026-08-15', '2026-09-30');
  const out: UploadRecord[] = spans.map(([from, to], i) => {
    const inside = deposits.filter((d) => d.accountId === CHASE.id && d.dateIso >= from && d.dateIso <= to);
    const total = round2(inside.reduce((s, d) => s + d.amount, 0));
    const uploadedIso = nextBusinessDay(to);
    return {
      id: `UP-${String(i + 1)}`,
      fileName: `chase-4417-${slugDay(from)}-${slugDay(to).replace(/^[a-z]+-/, '')}.csv`,
      accountId: CHASE.id,
      fromIso: from,
      toIso: to,
      linesRead: inside.length * 5 + 34 + (hash(`l|${from}`) % 38),
      kept: inside.length,
      keptTotal: total,
      matched: inside.length,
      dontMatch: 0,
      uploadedBy: UPLOADERS[hash(`u|${from}`) % UPLOADERS.length] ?? ADMIN_NAME,
      uploadedIso,
      uploadedLabel: uploadWhen(uploadedIso, `w|${from}`),
    };
  });
  const octMatched = deposits.filter((d) => d.accountId === CHASE.id && d.dateIso >= OCT_FILE.from && d.dateIso <= OCT_FILE.to);
  const octTotal = round2(octMatched.reduce((s, d) => s + d.amount, 0) + dontMatch.reduce((s, d) => s + d.amount, 0));
  out.push({
    id: 'UP-oct',
    fileName: OCT_UPLOAD_FILE,
    accountId: CHASE.id,
    fromIso: OCT_FILE.from,
    toIso: OCT_FILE.to,
    linesRead: 412,
    kept: octMatched.length + dontMatch.length,
    keptTotal: octTotal,
    matched: octMatched.length,
    dontMatch: dontMatch.length,
    uploadedBy: ADMIN_NAME,
    uploadedIso: '2026-10-14',
    uploadedLabel: 'Oct 14, 4:42 PM',
  });
  return out.sort((a, b) => (a.toIso < b.toIso ? 1 : a.toIso > b.toIso ? -1 : 0));
}

/** Points each deposit at the statement file that covers it. */
export function attachStatements(deposits: MatchedDeposit[], uploads: UploadRecord[]): MatchedDeposit[] {
  return deposits.map((d) => {
    const up = uploads.find((u) => u.accountId === d.accountId && d.dateIso >= u.fromIso && d.dateIso <= u.toIso);
    return up ? { ...d, statementFile: up.fileName, uploadedBy: up.uploadedBy, uploadedLabel: up.uploadedLabel } : d;
  });
}

// ---------------------------------------------------------------------------
// the whole history
// ---------------------------------------------------------------------------

export interface MatchedData {
  deposits: MatchedDeposit[];
  uploads: UploadRecord[];
  accounts: BankAccount[];
}

/** Deposits dated inside a statement and on/before the covered end, newest first. */
export function buildBaseDeposits(): MatchedDeposit[] {
  const real = realGroups().filter((g) => covered(g.iso) && g.iso <= COVER_TO).map(depositFromGroup);
  const checks = CHECK_SEEDS.filter((s) => covered(s.iso)).map(depositFromCheckSeed);
  return [...real, ...checks].sort((a, b) => (a.dateIso === b.dateIso ? (a.id < b.id ? -1 : 1) : a.dateIso < b.dateIso ? 1 : -1));
}

/** The deposit the "happy" upload brings in (Oct 16, one MetLife payment from the claim set). */
export function buildFreshUploadDeposits(): MatchedDeposit[] {
  return realGroups()
    .filter((g) => g.iso === '2026-10-16' && g.payer === 'MetLife')
    .map(depositFromGroup)
    .sort((a, b) => (a.dateIso === b.dateIso ? (a.id < b.id ? -1 : 1) : a.dateIso < b.dateIso ? 1 : -1));
}

/** The one deposit in that file that doesn't match (money in, nothing posted). */
export const FRESH_DONT_MATCH = { id: 'BD-6', iso: '2026-10-16', payer: 'Aetna', amount: 418.76 } as const;

export function buildMatchedData(): MatchedData {
  const base = buildBaseDeposits();
  const uploads = buildUploads(base, [
    { iso: DM.delta.iso, amount: DM.delta.amount },
    { iso: DM.metlife.iso, amount: DM.metlife.amount },
    { iso: DM.aetna.iso, amount: DM.aetna.amount },
    { iso: DM.cigna.iso, amount: DM.cigna.amount },
  ]);
  return { deposits: attachStatements(base, uploads), uploads, accounts: [CHASE] };
}
